import classNames from 'classnames';
import { AlertMessage } from 'components/common/Alert/AlertMessage';
import { OrderProcessingNotification } from 'components/common/OrderProcessingNotification/OrderProcessingNotification';
import { useInvoicingDetail } from 'providers/InvoicingDetailModuleProvider';
import { useEffect, useMemo, useState } from 'react';
import { getProductClassifications } from 'shared/api/product.api';
import { ToastNotificationType } from 'shared/enums';
import { useQueryFetcher } from 'shared/hooks/useQueryFetcher';
import { CreatedOrder } from 'shared/models';
import { ClassificationProduct } from 'shared/models/classification-product';
import { checkCaseIsInvoiced, getDateAndTimeInLongFormat, isAccountCreditHold } from 'shared/utils';
import { InvoicedDiscountSection } from './InvoicedDiscountSection/InvoicedDiscountSection';
import { InvoicedShippingDetail } from './ShippingSection/InvoicedShippingDetail/InvoicedShippingDetail';
import { ShippingSection } from './ShippingSection/ShippingSection';
import { VerifyCaseSection } from './VerifyCaseSection/VerifyCaseSection';

/**
 * Props interface for the InvoiceDetail component.
 */
interface InvoiceDetailProps {
  /**
   * Boolean indicating whether order is loading.
   */
  isLoading: boolean;
  /**
   * Boolean indicating whether shipping is loading.
   */
  isShippingLoading: boolean;

  orderBeingProcessed: CreatedOrder | undefined;

  /**
   * Optional function to refresh data.
   */
  onRefresh?: () => void;
}
/**
 * Component to display invoice details including case verification, shipping details, and discounts.
 * @param isLoading - Boolean indicating whether order is loading.
 * @param isShippingLoading - Boolean indicating whether shipping is loading.
 * @param onRefresh - Optional function to refresh data.
 */
const InvoiceDetail: React.FC<InvoiceDetailProps> = ({
  isLoading: invoicingLoading,
  isShippingLoading,
  onRefresh,
  orderBeingProcessed,
}) => {
  const { invoiceCases, account } = useInvoicingDetail();
  const firstInvoiceCase = invoiceCases[0];

  /**
   * As per LMS1-8647/LMS1-8648, invoice pending, invoiced and shipped orders are not eligible for discounts.
   */
  const isOrderInvoiced = useMemo(() => {
    const orderStatus = firstInvoiceCase?.status;
    return !!orderStatus && checkCaseIsInvoiced(orderStatus);
  }, [firstInvoiceCase?.status]);

  const isInvoicedCase = checkCaseIsInvoiced(firstInvoiceCase?.status);
  const { data: classificationsData } = useQueryFetcher(getProductClassifications);
  const [materialAndRestorationType, setMaterialAndRestorationType] = useState<ClassificationProduct[]>([]);
  const [isUserNotifiedOfPricingCalculationCompletion, setIsUserNotifiedOfPricingCalculationCompletion] =
    useState(true);
  useEffect(() => {
    if (classificationsData) {
      const classifications: ClassificationProduct[] = classificationsData.flatMap(item =>
        item.restorations.map(({ restorationName, productCode }) => ({
          restorationName,
          productCode,
          materialName: item.materialName,
        }))
      );
      setMaterialAndRestorationType(classifications);
    }
  }, [classificationsData, setMaterialAndRestorationType]);

  return (
    <div className="flex flex-col flex-grow mb-3">
      <div
        className={classNames('flex flex-col gap-3', {
          // Determine whether or not we are displaying an alert at the top and set padding accordingly.
          'p-6 pb-0 2xl:pb-6': !invoicingLoading && (isAccountCreditHold(account?.standing) || isInvoicedCase),
        })}
      >
        {!invoicingLoading && isAccountCreditHold(account?.standing) && (
          <AlertMessage
            message={`This case cannot be invoiced because the account is on credit hold.`}
            type={ToastNotificationType.Error}
            className="w-full"
          />
        )}
        {!invoicingLoading && isInvoicedCase && (
          <AlertMessage
            message={`Invoiced on ${getDateAndTimeInLongFormat(
              firstInvoiceCase.invoiceDate,
              firstInvoiceCase.utcConversionTimeZoneCode
            )} ${firstInvoiceCase.invoiceBy ? `by ${firstInvoiceCase.invoiceBy}` : ''}`}
            type={ToastNotificationType.Warning}
            className="w-full"
          />
        )}
        {firstInvoiceCase && (
          <OrderProcessingNotification
            orderMetaData={orderBeingProcessed?.metadata}
            orderNumber={orderBeingProcessed?.orderNumber || ''}
            onRefresh={onRefresh}
            isUsingToast={true}
            isUserNotifiedOfPricingCalculationCompletion={isUserNotifiedOfPricingCalculationCompletion}
          />
        )}
      </div>
      <div className="flex-grow 2xl:flex">
        <div className="flex-grow 2xl:w-7/12">
          {/* Verify Case Information */}
          <div className="p-6">
            <VerifyCaseSection isLoading={invoicingLoading} isInvoicedCase={isInvoicedCase} />
          </div>
        </div>
        <div className="border-t border-gray-300 border-l-0 mx-0 2xl:border-t-0 2xl:border-l 2xl:w-5/12">
          {/* Select shipping */}
          <div className="p-6 2xl:flex-none" id="shipping-detail">
            {isInvoicedCase ? (
              <InvoicedShippingDetail isLoading={isShippingLoading} />
            ) : (
              <ShippingSection isLoading={isShippingLoading} />
            )}
          </div>
          <div className="border-gray-300 border-b"></div>
          {/* Discount */}
          <div className="p-6 2xl:flex-none" id="coupons">
            <InvoicedDiscountSection
              isLoading={invoicingLoading}
              onRefresh={onRefresh}
              classifications={materialAndRestorationType}
              isDiscountDisabled={!!orderBeingProcessed || isOrderInvoiced}
              setSkipCompletedForBundle={(skip: boolean) => setIsUserNotifiedOfPricingCalculationCompletion(skip)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;
